import { useCallback, useEffect, useState } from 'react';
import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { signOutCurrentSession } from '../api/authApi.js';

function isPasswordRecoveryLocation() {
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'reset-password'
    || params.get('type') === 'recovery'
    || /type=recovery/i.test(window.location.hash);
}

export function useAuthSession() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(isPasswordRecoveryLocation);

  useEffect(() => {
    if (!hasSupabaseConfig || !supabase) {
      setLoading(false);
      return undefined;
    }

    let active = true;

    supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) console.error('Failed to restore auth session', error);
      setSession(data?.session ?? null);
      if (data?.session && isPasswordRecoveryLocation()) setPasswordRecovery(true);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      setSession(nextSession ?? null);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    await signOutCurrentSession();
  }, []);

  const finishPasswordRecovery = useCallback(() => {
    setPasswordRecovery(false);

    if (typeof window !== 'undefined') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return {
    session,
    user: session?.user ?? null,
    loading,
    passwordRecovery,
    signOut,
    finishPasswordRecovery,
  };
}
